import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Model } from 'survey-core';

import { loadServiceDetailsByService } from '../../../../store/directusService';
import { createFormParticipant, loadServiceConstants } from '../../../../store/participantService';
import {
    SERVICE_CONSTANTS_ERROR,
    SERVICES_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import { themeJson } from '../FormTheme';
import GenerateForm from '../GenerateForm';

import UCD604FormTemplate from './UCD604FormTemplate';

const CreateUCD604Form = ({
    currentForm,
    surveyModelData = {},
    participantsAdviser,
    onClose,
    generate = false
}) => {
    const dispatch = useDispatch();

    //LOCAL STATE
    const [fileName, setFileName] = useState('');
    const [surveyModel, setSurveyModel] = useState(new Model('{}'));
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [generatePdf, setGeneratePdf] = useState(false);

    //STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const formStatusDetails = useSelector(
        (state) => state.entities.directusService.formStatusDetails
    );
    const serviceConstants = useSelector(
        (state) => state.entities.participantService.serviceConstants
    );
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        const fileName = `${currentForm.name}_${format(new Date(), `dd_MMM_yyyy_HH_mm`)}`;
        setFileName(fileName);
    }, []);

    useEffect(() => {
        if (!('serviceId' in currentParticipant)) return;

        if (!(currentParticipant.serviceId in serviceConstants)) {
            dispatch(loadServiceConstants(currentParticipant.serviceId));
        }
        if (!(currentParticipant.serviceId in serviceDetailsById)) {
            dispatch(loadServiceDetailsByService([currentParticipant.serviceId]));
        }
    }, [currentParticipant]);

    useEffect(() => {
        if (
            !Object.keys(currentForm).length ||
            !(currentParticipant.serviceId in serviceConstants) ||
            !(currentParticipant.serviceId in serviceDetailsById) ||
            isFormComplete ||
            !participantsAdviser
        )
            return;

        const constants = serviceConstants[currentParticipant.serviceId];
        const serviceDetails = serviceDetailsById[currentParticipant.serviceId];
        const model = new Model(currentForm.data);
        model.showQuestionNumbers = true;
        model.applyTheme(themeJson);

        model.setValue('Q_1', `${currentParticipant?.firstName} ${currentParticipant?.lastName}`);
        model.setValue('Q_2', currentParticipant?.uniqueIdentifier);
        model.setValue('Q_3', '');
        model.setValue('Q_4', '');
        model.setValue('Q_5', '');
        model.setValue('Q_6', '');
        model.setValue('Q_7', ''); // to be configured
        model.setValue('Q_8', ''); // to be configured
        model.setValue('Q_9', '');
        model.setValue('Q_10', '');
        model.setValue('Q_11', '');
        model.setValue('Q_12', '');
        model.setValue('Q_13', '');
        model.setValue('Q_14', '');
        model.setValue('Q_15', '');
        model.setValue('Q_17', `${participantsAdviser.firstName || ''} ${participantsAdviser.lastName || ''}`);
        model.setValue('Q_18', constants.sanctionFormFromEmailAddress);
        model.setValue('Q_19', serviceDetails.company_name);

        const addressArr = [
            constants.formAddressLine1,
            constants.formAddressLine2,
            constants.formAddressLine3,
            constants.formAddressLine4,
            constants.formCity,
            constants.formCounty,
            constants.formPostcode
        ].filter((el) => el);

        model.setValue('Q_20', addressArr.join('\n'));
        model.setValue('Q_22', constants?.formPhoneNumber);

        model.onComplete.add((sender, options) => {
            console.debug('options', options);
            console.debug(JSON.stringify(sender.data, null, 3));
        });
        generate && setSurveyModel(model);
        setIsFormComplete(true);

        const payload = {
            participantId: currentParticipant.id,
            statusId: formStatusDetails.find((el) => el.name === 'Open')?.id || '',
            pdfName: currentForm.name,
            formId: currentForm.id,
            data: { ...model.data, ...surveyModelData }
        };
        dispatch(createFormParticipant(payload));
    }, [
        currentForm,
        participantsAdviser,
        serviceConstants,
        serviceDetailsById,
        isFormComplete
    ]);

    useEffect(() => {
        if (successMessage === `Form participant has been created successfully`) {
            generate ? setGeneratePdf(true) : onClose();
        }
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (!(currentParticipant.serviceId in serviceConstants)) return SERVICE_CONSTANTS_ERROR;
        if (!(currentParticipant.serviceId in serviceDetailsById)) return SERVICES_ERROR;
    };

    // EVENT HANDLERS
    const onFormExit = (isPdfGenerated = false) => onClose(isPdfGenerated);

    const onPdfGenerated = (isPdfGenerated) => {
        setGeneratePdf(false);
        onFormExit(isPdfGenerated);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div>
            {generate && generatePdf && (
                <GenerateForm
                    fileName={fileName}
                    formCode={currentForm.code}
                    ptCode={currentParticipant.ptCode}
                    userCode={loggedInUser?.code || ''}
                    onPdfGenerated={onPdfGenerated}>
                    <UCD604FormTemplate
                        surveyAnswers={{ ...surveyModel.data, ...surveyModelData }}
                    />
                </GenerateForm>
            )}
        </div>
    );
};

CreateUCD604Form.propTypes = {
    currentForm: PropTypes.object,
    surveyModelData: PropTypes.object,
    participantsAdviser: PropTypes.object,
    onClose: PropTypes.func,
    generate: PropTypes.bool
};

export default CreateUCD604Form;
